import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { Home, Settings, Sun, Map, Loader2, Cloud, CloudOff, RefreshCw, Inbox, WifiOff, AlertTriangle, X, Bell, CheckCheck } from 'lucide-react';
import { ToastContainer } from './components/ui/Toast';
import { AuthProvider } from './components/auth/AuthProvider';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LoginPage } from './components/auth/LoginPage';
import { SignUpPage } from './components/auth/SignUpPage';
import { ForgotPassword } from './components/auth/ForgotPassword';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useSyncedState, SyncStatus } from './hooks/useSyncedState';
import { useAuth } from './hooks/useAuth';
import { useSessionActivity } from './hooks/useSessionActivity';
import { useNotifications } from './hooks/useNotifications';
import { saveOnboardingStatus, saveGoal } from './lib/supabase/dataService';
import { TransitionPlaybook, UserPlaybook, Goal, UserProfile, LifestylePreferences, LifeValues, FamilyMember, CareerPath } from './types';
import { generateSeededGoals } from './utils/goalSeeding';
import { format, addMonths, formatDistanceToNow } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

// Lazy load page components for code splitting (reduces initial bundle by ~40%)
const LandingPage = lazy(() => import('./components/LandingPage'));
const Dashboard = lazy(() => import('./components/Dashboard').then(m => ({ default: m.Dashboard })));
const ChatInterface = lazy(() => import('./components/ChatInterface').then(m => ({ default: m.ChatInterface })));
const GoalsPage = lazy(() => import('./components/GoalsPage').then(m => ({ default: m.GoalsPage })));
const FinancesPage = lazy(() => import('./components/FinancesPage').then(m => ({ default: m.FinancesPage })));
const TasksPage = lazy(() => import('./components/TasksPage').then(m => ({ default: m.TasksPage })));
const CheckInPage = lazy(() => import('./components/CheckInPage').then(m => ({ default: m.CheckInPage })));
const SettingsPage = lazy(() => import('./components/SettingsPage').then(m => ({ default: m.SettingsPage })));
const AIRiskQuizPage = lazy(() => import('./components/AIRiskQuizPage').then(m => ({ default: m.AIRiskQuizPage })));
const RunwayCalculator = lazy(() => import('./components/RunwayCalculator').then(m => ({ default: m.RunwayCalculator })));
const PlaybooksPage = lazy(() => import('./components/playbooks/PlaybooksPage').then(m => ({ default: m.PlaybooksPage })));
const PlaybookDetailPage = lazy(() => import('./components/PlaybookDetailPage').then(m => ({ default: m.PlaybookDetailPage })));
const GeneratedPlaybookDetailPage = lazy(() => import('./components/GeneratedPlaybookDetailPage').then(m => ({ default: m.GeneratedPlaybookDetailPage })));
const SkillGapAnalyzer = lazy(() => import('./components/SkillGapAnalyzer').then(m => ({ default: m.SkillGapAnalyzer })));
const LifePlanningPage = lazy(() => import('./components/LifePlanningPage').then(m => ({ default: m.LifePlanningPage })));
const ApprovalQueuePage = lazy(() => import('./components/ApprovalQueue').then(m => ({ default: m.ApprovalQueuePage })));
const AdminPage = lazy(() => import('./components/admin/AdminPage').then(m => ({ default: m.AdminPage })));
const OnboardingFlow = lazy(() => import('./components/onboarding/OnboardingFlow').then(m => ({ default: m.OnboardingFlow })));
const MockInterviewPage = lazy(() => import('./components/interview/MockInterviewPage').then(m => ({ default: m.MockInterviewPage })));
const JobSearchPage = lazy(() => import('./components/jobs/JobSearchPage').then(m => ({ default: m.JobSearchPage })));
const MarketDashboard = lazy(() => import('./components/market/MarketDashboard').then(m => ({ default: m.MarketDashboard })));
const DocumentsPage = lazy(() => import('./components/documents/DocumentsPage').then(m => ({ default: m.DocumentsPage })));
const DataExportPage = lazy(() => import('./components/export/DataExportPage').then(m => ({ default: m.DataExportPage })));
const NetworkMap = lazy(() => import('./components/network/NetworkMap').then(m => ({ default: m.NetworkMap })));
const WebhookManager = lazy(() => import('./components/webhooks/WebhookManager').then(m => ({ default: m.WebhookManager })));
const PricingPage = lazy(() => import('./components/billing/PricingPage').then(m => ({ default: m.PricingPage })));

// Loading spinner component for Suspense fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-full gradient-mesh">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
        <p className="text-text-secondary text-sm">Loading...</p>
      </div>
    </div>
  );
}

// Sync status indicator component
function SyncIndicator({ syncStatus }: { syncStatus: SyncStatus }) {
  if (syncStatus.isLoading) {
    return (
      <div className="fixed top-2 right-2 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface/80 backdrop-blur-sm border border-border text-xs">
        <Loader2 className="w-3 h-3 text-gold animate-spin" />
        <span className="text-text-secondary">Loading...</span>
      </div>
    );
  }

  if (syncStatus.error) {
    return (
      <div className="fixed top-2 right-2 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 backdrop-blur-sm border border-red-500/30 text-xs">
        <CloudOff className="w-3 h-3 text-red-400" />
        <span className="text-red-400">Offline</span>
      </div>
    );
  }

  if (syncStatus.isSyncing) {
    return (
      <div className="fixed top-2 right-2 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface/80 backdrop-blur-sm border border-border text-xs">
        <RefreshCw className="w-3 h-3 text-gold animate-spin" />
        <span className="text-text-secondary">Syncing...</span>
      </div>
    );
  }

  if (syncStatus.lastSynced) {
    return (
      <div className="fixed top-2 right-2 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 backdrop-blur-sm border border-green-500/30 text-xs opacity-0 hover:opacity-100 transition-opacity">
        <Cloud className="w-3 h-3 text-green-400" />
        <span className="text-green-400">Synced</span>
      </div>
    );
  }

  return null;
}

type Page = 'dashboard' | 'chat' | 'goals' | 'finances' | 'tasks' | 'checkin' | 'settings'
  | 'ai-risk' | 'runway' | 'playbooks' | 'playbook-detail' | 'skills' | 'life-planning' | 'queue' | 'admin'
  | 'interview' | 'jobs' | 'market' | 'documents' | 'export' | 'network' | 'webhooks' | 'pricing';

// Onboarding wrapper component to handle completion
function OnboardingWrapper() {
  const [, setState] = useSyncedState();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleOnboardingComplete = async (data: {
    profile: Partial<UserProfile>;
    lifestyle: Partial<LifestylePreferences>;
    values: Partial<LifeValues>;
    familyMembers: FamilyMember[];
    coachArchetype: string | null;
    biggestChallenge: string;
  }) => {
    if (!user) return;

    // CRITICAL: Save onboarding status to DB FIRST, before updating local state
    // This prevents the race condition where navigation happens before DB is updated
    const saveResult = await saveOnboardingStatus(user.id, true);
    if (!saveResult.success) {
      console.error('Failed to save onboarding status:', saveResult.error);
      // Still continue - the sync will retry
    }

    // Seed goals from chosen career paths
    const chosenPaths = (data.profile.chosenPaths as CareerPath[]) ?? [];
    const targetYear = (data.profile.targetYear as number) ?? new Date().getFullYear() + 3;
    const seededGoals = generateSeededGoals(
      chosenPaths.filter(p => p !== 'undecided'),
      targetYear
    );

    // Update local state with collected onboarding data + seeded goals
    setState(prev => ({
      ...prev,
      profile: {
        ...prev.profile,
        ...data.profile,
        startDate: prev.profile.startDate || new Date().toISOString(),
      },
      goals: [...prev.goals, ...seededGoals],
      lifestylePreferences: {
        ...prev.lifestylePreferences,
        ...data.lifestyle,
        familyLocations: data.familyMembers,
      },
      lifeValues: {
        ...prev.lifeValues,
        ...data.values,
      },
      onboardingComplete: true,
    }));

    // Save seeded goals to DB (don't await - not critical for navigation)
    if (seededGoals.length > 0) {
      Promise.all(seededGoals.map(goal => saveGoal(user.id, goal))).catch(err => {
        console.error('Error saving seeded goals:', err);
      });
    }

    // Save coach archetype to Supabase if selected (don't await - not critical for navigation)
    if (data.coachArchetype) {
      import('./lib/personality/personalityService').then(({ applyArchetypePreset }) => {
        applyArchetypePreset(user.id, data.coachArchetype as import('./types/coachPersonality').CoachArchetype).then(() => {
          console.log('Coach archetype saved');
        }).catch((err) => {
          console.error('Error saving coach archetype:', err);
        });
      });
    }

    // Navigate to dashboard AFTER DB is updated
    navigate('/dashboard');
  };

  return (
    <OnboardingFlow
      onComplete={handleOnboardingComplete}
      initialProfile={user?.user_metadata}
    />
  );
}

function MainApp() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState, syncStatus] = useSyncedState();
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');
  const [selectedPlaybook, setSelectedPlaybook] = useState<TransitionPlaybook | null>(null);
  const [selectedGeneratedPlaybook, setSelectedGeneratedPlaybook] = useState<unknown | null>(null);
  const { trackActivity } = useSessionActivity();
  const { notifications, unreadCount, markAsRead, markAllRead } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showStaleWarning, setShowStaleWarning] = useState(false);

  // Close notification dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Show stale sync warning when detected
  useEffect(() => {
    if (syncStatus.staleSync) {
      setShowStaleWarning(true);
    }
  }, [syncStatus.staleSync]);

  // Track activity on page navigation
  useEffect(() => {
    trackActivity();
  }, [currentPage, trackActivity]);

  // Redirect to onboarding if not complete
  useEffect(() => {
    // Wait for data to load before checking onboarding status
    if (!syncStatus.isLoading && !state.onboardingComplete) {
      navigate('/onboarding', { replace: true });
    }
  }, [syncStatus.isLoading, state.onboardingComplete, navigate]);

  // Check if user has done today's check-in
  const today = format(new Date(), 'yyyy-MM-dd');
  const hasCheckedInToday = state.dailyCheckIns.some(c => c.date === today);

  // Handle playbook adoption
  const handleAdoptPlaybook = (playbookId: string, playbook: TransitionPlaybook) => {
    // Check if already adopted
    const alreadyAdopted = state.userPlaybooks?.some(up => up.playbookId === playbookId);
    if (alreadyAdopted) {
      handleNavigate('dashboard');
      return;
    }

    // Create UserPlaybook entry
    const userPlaybook: UserPlaybook = {
      id: uuidv4(),
      userId: user?.id || 'anonymous',
      playbookId: playbookId,
      status: 'active',
      startedAt: new Date().toISOString(),
      progressPercent: 0,
      currentPhase: 1,
      playbook: playbook,
    };

    // Create a goal for this playbook
    const targetDate = addMonths(new Date(), playbook.timelineMonths);
    const playbookGoal: Goal = {
      id: uuidv4(),
      title: `Complete: ${playbook.name}`,
      description: `Transition from ${playbook.fromRole} to ${playbook.toRole} using the ${playbook.name} playbook.`,
      category: 'career',
      targetDate: format(targetDate, 'yyyy-MM-dd'),
      status: 'in-progress',
      progress: 0,
      milestones: [
        { id: uuidv4(), title: 'Start playbook', completed: true, completedAt: new Date().toISOString() },
        { id: uuidv4(), title: 'Complete Phase 1', completed: false },
        { id: uuidv4(), title: 'Reach 50% progress', completed: false },
        { id: uuidv4(), title: 'Complete all required skills', completed: false },
        { id: uuidv4(), title: 'Complete playbook', completed: false },
      ],
      priority: 'high',
      archived: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Update state with new playbook and goal
    setState(prev => ({
      ...prev,
      userPlaybooks: [...(prev.userPlaybooks || []), userPlaybook],
      goals: [...prev.goals, playbookGoal],
    }));

    handleNavigate('dashboard');
  };

  const handleNavigate = (page: string, data?: unknown) => {
    if (page === 'playbook-detail' && data) {
      // Handle both static and generated playbooks
      const playbookData = data as { playbook?: unknown; isGenerated?: boolean } | TransitionPlaybook;
      if ('isGenerated' in playbookData && playbookData.isGenerated) {
        setSelectedGeneratedPlaybook(playbookData.playbook);
        setSelectedPlaybook(null);
      } else {
        setSelectedPlaybook(playbookData as TransitionPlaybook);
        setSelectedGeneratedPlaybook(null);
      }
    }
    setCurrentPage(page as Page);
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard state={state} onNavigate={handleNavigate} />;
      case 'chat':
        return <ChatInterface state={state} onStateChange={setState} />;
      case 'goals':
        return <GoalsPage state={state} onStateChange={setState} />;
      case 'finances':
        return <FinancesPage state={state} onStateChange={setState} />;
      case 'tasks':
        return <TasksPage state={state} onStateChange={setState} />;
      case 'checkin':
        return <CheckInPage state={state} onStateChange={setState} onNavigate={handleNavigate} />;
      case 'settings':
        return <SettingsPage state={state} onStateChange={setState} onNavigate={handleNavigate} />;
      case 'admin':
        return <AdminPage onNavigate={handleNavigate} />;
      case 'ai-risk':
        return <AIRiskQuizPage state={state} onStateChange={setState} onNavigate={handleNavigate} />;
      case 'runway':
        return <RunwayCalculator state={state} onStateChange={setState} onNavigate={handleNavigate} />;
      case 'playbooks':
        return <PlaybooksPage state={state} onNavigate={handleNavigate} />;
      case 'playbook-detail':
        // Handle generated playbooks first
        if (selectedGeneratedPlaybook) {
          return (
            <GeneratedPlaybookDetailPage
              playbook={selectedGeneratedPlaybook}
              onNavigate={handleNavigate}
            />
          );
        }
        // Handle static/legacy playbooks
        return selectedPlaybook ? (
          <PlaybookDetailPage
            playbook={selectedPlaybook}
            onNavigate={handleNavigate}
            onAdopt={(id) => handleAdoptPlaybook(id, selectedPlaybook)}
          />
        ) : (
          <PlaybooksPage state={state} onNavigate={handleNavigate} />
        );
      case 'skills':
        return <SkillGapAnalyzer state={state} onStateChange={setState} onNavigate={handleNavigate} />;
      case 'life-planning':
        return <LifePlanningPage state={state} onStateChange={setState} userId={user?.id || ''} />;
      case 'queue':
        return <ApprovalQueuePage onNavigate={handleNavigate} />;
      case 'interview':
        return <MockInterviewPage state={state} onStateChange={setState} onNavigate={handleNavigate} />;
      case 'jobs':
        return <JobSearchPage state={state} onStateChange={setState} onNavigate={handleNavigate} />;
      case 'market':
        return <MarketDashboard state={state} onStateChange={setState} onNavigate={handleNavigate} />;
      case 'documents':
        return <DocumentsPage />;
      case 'export':
        return <DataExportPage state={state} />;
      case 'network':
        return <NetworkMap state={state} onStateChange={setState} onNavigate={handleNavigate} />;
      case 'webhooks':
        return <WebhookManager />;
      case 'pricing':
        return <PricingPage currentTier={state.profile.subscriptionTier || 'free'} onSelectPlan={() => handleNavigate('settings')} />;
      default:
        return <Dashboard state={state} onNavigate={handleNavigate} />;
    }
  };

  const navItems: { id: Page; icon: React.ReactNode; label: string; badge?: boolean }[] = [
    { id: 'dashboard', icon: <Home className="w-5 h-5" />, label: 'Home' },
    { id: 'queue', icon: <Inbox className="w-5 h-5" />, label: 'Queue' },
    { id: 'playbooks', icon: <Map className="w-5 h-5" />, label: 'Playbooks' },
    { id: 'checkin', icon: <Sun className="w-5 h-5" />, label: 'Check-in', badge: !hasCheckedInToday },
    { id: 'settings', icon: <Settings className="w-5 h-5" />, label: 'Settings' },
  ];

  return (
    <div className="h-screen flex flex-col bg-base bg-orbs">
      {/* Offline Banner */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500/10 border-b border-amber-500/20 px-4 py-2 flex items-center justify-center gap-2">
          <WifiOff className="w-4 h-4 text-amber-400" />
          <span className="text-sm text-amber-400">You&apos;re offline. Changes will sync when you reconnect.</span>
        </div>
      )}

      {/* Stale Sync Warning */}
      {showStaleWarning && isOnline && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500/10 border-b border-amber-500/20 px-4 py-3 flex items-center justify-center gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <span className="text-sm text-amber-400">It&apos;s been over 7 days since your last sync. Your data may be out of date. Please connect to sync your latest data.</span>
          <button
            onClick={() => setShowStaleWarning(false)}
            className="ml-2 p-1 rounded-lg hover:bg-amber-500/20 transition-colors flex-shrink-0"
            aria-label="Dismiss stale sync warning"
          >
            <X className="w-4 h-4 text-amber-400" />
          </button>
        </div>
      )}

      {/* Sync Status Indicator */}
      <SyncIndicator syncStatus={syncStatus} />

      {/* Notification Bell (U-137) */}
      <div ref={notificationRef} className="fixed top-2 right-14 z-50">
        <button
          onClick={() => setShowNotifications(prev => !prev)}
          className="relative flex items-center justify-center w-8 h-8 rounded-full bg-surface/80 backdrop-blur-sm border border-border hover:border-gold/30 transition-all"
          aria-label={`Notifications${unreadCount > 0 ? ` - ${unreadCount} unread` : ''}`}
        >
          <Bell className="w-3.5 h-3.5 text-text-secondary" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-ruby rounded-full flex items-center justify-center">
              <span className="text-[9px] font-bold text-white">{unreadCount > 9 ? '9+' : unreadCount}</span>
            </span>
          )}
        </button>

        {/* Notification Dropdown */}
        {showNotifications && (
          <div className="absolute top-full right-0 mt-2 w-80 max-h-96 glass-card gold-border rounded-2xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="p-3 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-text-primary">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllRead()}
                  className="flex items-center gap-1 text-[10px] text-gold hover:text-gold-light transition-colors uppercase tracking-wider font-medium"
                >
                  <CheckCheck className="w-3 h-3" />
                  Mark all read
                </button>
              )}
            </div>

            {/* Notification List */}
            <div className="overflow-y-auto max-h-72">
              {notifications.length === 0 ? (
                <div className="p-6 text-center">
                  <Bell className="w-8 h-8 text-text-muted mx-auto mb-2 opacity-40" />
                  <p className="text-sm text-text-muted">No notifications yet</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <button
                    key={notification.id}
                    onClick={() => {
                      if (!notification.read) markAsRead(notification.id);
                    }}
                    className={`w-full text-left p-3 border-b border-white/5 hover:bg-white/[0.04] transition-colors ${
                      !notification.read ? 'bg-gold/5' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {!notification.read && (
                        <span className="w-2 h-2 rounded-full bg-gold mt-1.5 flex-shrink-0" />
                      )}
                      <div className={`flex-1 ${notification.read ? 'ml-4' : ''}`}>
                        <p className="text-sm font-medium text-text-primary leading-tight">{notification.title}</p>
                        <p className="text-xs text-text-muted mt-0.5 line-clamp-2">{notification.body}</p>
                        <p className="text-[10px] text-text-muted mt-1">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <ErrorBoundary
          pageName={currentPage}
          onError={(error, errorInfo) => {
            // Log to console in development, could send to error tracking service in production
            console.error(`Error on ${currentPage} page:`, error, errorInfo);
          }}
        >
          <Suspense fallback={<PageLoader />}>
            {renderPage()}
          </Suspense>
        </ErrorBoundary>
      </main>

      {/* Bottom Navigation - Glass Effect */}
      <nav
        className="glass-card border-t-0 border-l-0 border-r-0 px-4 py-3 safe-area-pb"
        aria-label="Main navigation"
        role="navigation"
      >
        <div className="flex justify-around items-center max-w-md mx-auto">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setCurrentPage(item.id)}
              aria-current={currentPage === item.id ? 'page' : undefined}
              aria-label={`${item.label}${item.badge ? ' - action required' : ''}`}
              className={`relative flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-base ${
                currentPage === item.id
                  ? 'text-gold'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {/* Icon with glow effect when active */}
              <span className={`transition-all duration-300 ${
                currentPage === item.id ? 'drop-shadow-[0_0_8px_rgba(212,175,55,0.5)]' : ''
              }`} aria-hidden="true">
                {item.icon}
              </span>

              {/* Label */}
              <span className={`text-[10px] font-medium tracking-wide uppercase transition-all duration-300 ${
                currentPage === item.id ? 'opacity-100' : 'opacity-70'
              }`}>
                {item.label}
              </span>

              {/* Active indicator dot */}
              {currentPage === item.id && (
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-gold rounded-full shadow-[0_0_8px_rgba(212,175,55,0.8)]" aria-hidden="true" />
              )}

              {/* Badge for check-in */}
              {item.badge && (
                <span className="absolute top-0 right-2 w-2 h-2 bg-gold rounded-full animate-pulse-glow" aria-hidden="true" />
              )}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* Global Toast Container */}
        <ToastContainer />
        <Routes>
          {/* Public routes */}
          <Route
            path="/"
            element={
              <Suspense fallback={<PageLoader />}>
                <LandingPage />
              </Suspense>
            }
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Onboarding route (protected) */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <OnboardingWrapper />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            }
          />

          {/* 404 catch-all */}
          <Route
            path="*"
            element={
              <div className="h-screen flex flex-col items-center justify-center bg-base bg-orbs text-center px-6">
                <h1 className="text-6xl font-display font-bold text-gold mb-4">404</h1>
                <p className="text-xl text-text-primary mb-2">Page not found</p>
                <p className="text-text-muted mb-8">The page you're looking for doesn't exist.</p>
                <a
                  href="/"
                  className="px-6 py-3 bg-gold text-base font-semibold rounded-xl hover:bg-gold-light transition-colors"
                >
                  Go Home
                </a>
              </div>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
